<template>
    <div ref="buyBox" class="flex flex-col gap-6 w-full">
        <!-- Title -->
        <div>
            <h1 v-if="product?.name" class="text-3xl font-semibold">
                {{ product?.name }}
            </h1>

            <div v-html="product?.customFields?.teaser_description" />
        </div>

        <!-- Price -->
        <div>
            <ProductPrice
                :price="product?.price"
                :variant-name="variantName"
                :price-info="product?.customFields?.savings_text"
                :special-price="product?.customFields?.special_price"
            />

            <div v-if="product?.sku" class="leading-5 text-base">
                {{ t('product.buyBox.sku') }}: {{ product?.sku }}
            </div>

            <div class="text-primary-content leading-4 text-xs">
                <div v-if="product?.purchaseUnit && product?.unit?.translated?.name && product?.purchaseUnit > '0' || product.price?.referencePrice != null" class="flex gap-1">
                    <ProductPriceUnit :product="product" />
                </div>

                <div>
                    {{ t('product.buyBox.tax') }}
                    <MiscLink :to="t('product.buyBox.plusShippingLink')" class="underline">
                        {{ t('product.buyBox.plusShipping') }}
                    </MiscLink>
                </div>
            </div>
        </div>

        <!-- Abo Select -->
        <ProductDetailAboSelect
            v-if="product?.customFields?.lg_abo_product_enabled && locale === 'de'"
            ref="aboSelectRef"
            :product-data="product"
        />

        <!-- Cart Button -->
        <div
            v-if="hasMinimumPrice"
            class="flex justify-between items-end gap-2 w-full"
            :class="{
                'out-of-stock': product?.stock < 1,
                'flex-nowrap': !setInformation?.productSet || setInformation?.productSet?.defined,
                'flex-wrap': setInformation?.productSet && !setInformation?.productSet?.defined
            }"
        >
            <button v-if="(setInformation?.productSet && !setInformation?.productSet?.defined) || product?.definedSet === false" class="btn btn-primary grayscale w-full md:w-1/2 lg:w-fit" @click="jumpToSetProducts">
                {{ t('product.buyBox.chooseSetProducts') }}
            </button>

            <div v-else class="form-control h-fit">
                <label :for="'quantityInput' + product.id" class="label pl-0 pb-2!">
                    <span class="label-text text-lg font-semibold text-link">
                        {{ t('product.buyBox.quantity') }}
                    </span>
                </label>

                <ProductQtySelector
                    :id="product.id"
                    class="qty-selector h-12"
                    :class="{'pointer-events-none grayscale opacity-50 hover:cursor-not-allowed' : product?.stock < 1 || product?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_not_orderable'}"
                    :qty="qty"
                    :max="product?.calculatedMaxPurchase != null ? product?.calculatedMaxPurchase : 100"
                    :input-disabled="false"
                    @update:qty="qty = $event"
                />
            </div>

            <button
                ref="buyButton"
                class="btn btn-buy flex-grow h-12 active add-to-cart"
                :disabled="loading || !setProductConfigured || product?.stock < 0 || product?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_not_orderable'"
                @click="onClickAddToCart(qty, product?.id)"
            >
                <span v-if="loading" class="loading" />
                <div v-else class="flex gap-2 items-center">
                    {{ t('product.buyBox.addToCart') }}
                    <MiscIcon
                        :name="'cart-box-arrow-down'"
                        :size="'lg'"
                    />
                </div>
            </button>

            <template v-if="(setInformation?.productSet && !setInformation?.productSet?.defined) || product?.definedSet === false">
                <ProductDetailStickyBuyButton
                    v-if="showStickyButton"
                    :product-data="product"
                    :set-product-configured="setProductConfigured"
                />
            </template>

            <template v-else>
                <div v-if="showStickyButton" class="fixed bottom-8 left-2/4 -translate-y-2/4 -translate-x-2/4 z-50">
                    <button class="btn btn-buy text-center font-semibold text-lg flex-grow border-none" @click="scrollToTop">
                        <span>{{ t('product.buyBox.buyNow') }}</span>
                        <MiscIcon name="chevron-up" size="md" />
                    </button>
                </div>
            </template>
        </div>

        <!-- Delivery status -->
        <div v-if="product?.deliveryTimeCustomFields" class="flex">
            <div
                class="flex gap-2 items-center"
                :class="{
                    'text-success': product?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_orderable',
                    'text-sale': product?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_not_orderable',
                    'text-sold-out': product?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_limited'
                }"
            >
                <MiscIcon
                    :name="'circlecheck-solid'"
                    :size="'md'"
                />
                <div class="text-lg">
                    <span v-if="product?.deliveryTimeCustomFields?.availability_text" class="font-semibold">{{ product?.deliveryTimeCustomFields?.availability_text }}</span>
                    <span v-if="product?.deliveryTimeCustomFields?.availability_additional_text">, {{ product?.deliveryTimeCustomFields?.availability_additional_text }}</span>
                </div>
            </div>
        </div>

        <!-- Variants -->
        <ProductDetailVariants
            v-if="Array.isArray(product?.variants) || Array.isArray(product?.variantCrossSelling?.elements)"
            :variants="product?.variants"
            :default-options="product?.defaultOptions || []"
            :parent-id="product?.parentId"
            :variant-cross-selling="product?.variantCrossSelling"
            @loading="variantLoading = $event"
        />

        <!-- Promotion -->
        <ProductDetailBuyBoxPromotion :product-data="product" />

        <!-- Additional Informations -->
        <div class="flex-col flex pt-2 gap-1">
            <div class="text-lg font-semibold pb-2 text-headline">
                {{ t('product.buyBox.adviceAndOptions') }}
            </div>
            <div>
                <WishlistProductToggleButton
                    class="underline"
                    :product="product"
                    :text-position="'right'"
                    :icon-size="'xl'"
                />
            </div>
            <div v-if="product?.customFields?.is_eco" class="flex flex-row items-center gap-1">
                <MiscIcon
                    :name="'eco-label'"
                    :size="'xl'"
                    class="text-white"
                />
                <div>
                    <span>{{ t('product.buyBox.eco') }}</span>
                    <span v-if="product?.customFields?.eco_control_number">&nbsp;{{ product?.customFields?.eco_control_number }}</span>
                    <span v-if="product.customFields?.country_of_origin">, {{ t('product.buyBox.origin') }}: {{ product.customFields?.country_of_origin }}</span>
                    <span v-if="product?.customFields?.eco_agriculture">, {{ product?.customFields?.eco_agriculture }}</span>
                    <span v-if="product?.customFields?.is_peaceful_agriculture">, <MiscLink class="underline" :to="t('product.buyBox.linkToPeacefulAgriculture')">{{ t('product.buyBox.linkTextToPeacefulAgriculture') }}</MiscLink>
                    </span>
                </div>
            </div>
            <div class="flex flex-row items-center gap-1">
                <MiscIcon
                    :name="'chat'"
                    :size="'xl'"
                    class="min-w-8"
                />
                <div class="inline-block">
                    {{ t('product.buyBox.tip1') }}
                    <MiscLink class="underline" to="/abo-service">
                        {{ t('product.buyBox.sub') }}
                    </MiscLink>
                    {{ t('product.buyBox.tip2') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import type { HblProduct } from '@/utils/types'
import {useCart, useNotification, useCurrency, useSetProduct, useNuxtApp, useDrawer} from '#imports'

const { t, locale } = useI18n()

const props = defineProps<{
    productData: HblProduct
}>()

const product = ref(props.productData)
const qty = ref<number>(1)
const cartStore = useCart()
const { loading: cartLoading, error: cartError } = storeToRefs(cartStore)
const { addToCart } = cartStore
const { showNotification } = useNotification()
const variantLoading = ref(false)
const loading = computed(() => {
    return cartLoading.value || variantLoading.value
})
const buyBox = ref<HTMLElement | null>(null)
const buyButton = ref<HTMLElement | null>(null)
const aboSelectRef = ref<any>(null)
const showStickyButton = ref(false)
const hasScrolled = ref(false)

const { toggleDrawer } = useDrawer()

const jumpToSetProducts = function() {
    document.getElementById('set-products').scrollIntoView({
        behavior: 'smooth'
    })
}
const setProductStore = useSetProduct()
const { configuredSet, setInformation, selectedProductCount } = storeToRefs(setProductStore)

const setProductConfigured = computed(() => {
    if(configuredSet.value === null && product.value.definedSet != null) {
        return product.value.definedSet
    }

    // todo: need field for product to determine if product is a set product
    //  right now we cant determine it before the set product info is loaded
    if(configuredSet.value === null) {
        return true
    }

    if(setInformation.value.productSet.defined) {
        return true
    }

    return setInformation?.value?.productSet?.count === selectedProductCount?.value
})

async function onClickAddToCart (qty: number, id: string) {
    // Get abo payload if abo is selected
    const aboPayload = aboSelectRef.value?.aboPayload || null

    await addToCart(qty, id, aboPayload)

    if (!cartError.value) {
        $hblBus.$emit('addToCart', { qty, product: product?.value })
    }

    toggleDrawer('cart', 'right')
}

const currentVariant = computed(() => {
    if(product?.value?.variants != null)
    {
        const temp = product?.value?.variants[0]?.options?.filter( el => {
            return el.id == product?.value?.defaultOptions[0]
        })
        return temp[0]?.name
    }

    return null
})

const variantName = computed(() => {
    if (currentVariant.value) {
        return currentVariant.value;
    }

    if(product.value?.packUnit) {
        return product.value?.packUnit
    }

    if (product?.value?.purchaseUnit && product?.value?.purchaseUnit !== '0') {
        return product?.value?.purchaseUnit?.toLocaleString('de-DE') + ' ' + product?.value?.unit?.translated?.name
    }

    return null
});

watch(cartError, (value) => {
    showNotification(value, 'error', true)
})

const { $hblBus } = useNuxtApp()
$hblBus.$on('productVariantChanged', eventListenerBuyBox)

function eventListenerBuyBox ({ data }: { data: HblProduct }) {
    // Merge all data but the variants with the product data
    const { variants, ...productData } = data
    product.value = Object.assign(product?.value, productData)
}

onBeforeUnmount(() => {
    $hblBus.$off('productVariantChanged', eventListenerBuyBox)
})

const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
};

const handleScroll = () => {
    if (!hasScrolled.value && window.scrollY > 10) {
        hasScrolled.value = true
    }
}

const hasMinimumPrice = computed(() => {
    return Number(product.value?.price?.regularPrice) > 0.01
})

onMounted(() => {
    if (buyBox.value) {
        const observer = new IntersectionObserver(
            (entries) => {
                const isVisible = entries[0].isIntersecting

                if (isVisible) {
                    showStickyButton.value = false
                } else if (hasScrolled.value) {
                    showStickyButton.value = true
                }
            },
            {
                threshold: 0,
                rootMargin: '-400px 0px 0px 0px' // This will trigger the button to appear 100px earlier
            }
        )

        observer.observe(buyBox.value)

        onUnmounted(() => {
            if (buyBox.value) {
                observer.unobserve(buyBox.value)
            }
        })
    }

    window.addEventListener('scroll', handleScroll)

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll)
    })

    setProductStore.resetSet()
})
</script>
